import {useCallback, useEffect, useRef} from 'react'
import FilterDropdown, {
  FILTER_SELECT_ALL,
  SelectAllDropdownItem,
} from './FilterDropdown.js'
import {simpleFilter} from '../tokenizeOptions.js'
import {
  getWarehouseName,
  warehouseOptionsSelector,
  warehouseOptionTokensSelector,
} from '../../data/warehouses.js'
import {updateForm, useAutoForm, useSelector} from '../../store.js'
import {useZropdown, useZropdownElement} from './Zropdown.js'
import {currentDropdownSelector} from '../../redux/selectors/ui/index.js'
import {showWarehouseModal} from '../../ordoro/Modals/WarehouseModal.js'
import DropdownItem from './Dropdown/DropdownItem.js'
import Icon from '../icons/Icon.js'
import className from '../className.js'
import Checkbox from './Checkbox.js'

function WarehouseDropdownItem({option, isFocused, onSelect}) {
  const warehouse = option.entity
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <DropdownItem>
      <div
        className={`wrap--dropdown-item flex--justify-nowrap label--selectable label--selectable-pt-pb-0 margin-bottom-0 ${
          isFocused ? 'list__link--dropdown-arrow-focus' : ''
        }`}
      >
        <button
          className="btn--link list__link--dropdown list__link--warehouse"
          type="button"
          onClick={(event) => onSelect(option, event)}
        >
          <span className="btn__text">{getWarehouseName(warehouse)}</span>
        </button>
        <span className="wrap--additional-buttons">
          <button
            className="btn btn--link no-underline margin-right-3"
            type="button"
            title="Edit Preset"
            onClick={() => showWarehouseModal({warehouseID: warehouse.id})}
            data-dropdown-prevent-close={false}
          >
            <Icon name="pencil" className="fs-00" />
          </button>
        </span>
      </div>
    </DropdownItem>
  )
}

function WarehouseCheckboxItem({
  option,
  selected,
  disabled,
  isFocused,
  onSelect,
}) {
  const warehouse = option.entity
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li
      ref={ref}
      className="list__item list__item--dropdown wrap--dropdown-item wrap--dropdown-item-np"
    >
      <span
        className={className`label--selectable label--selectable-pt-pb-0 flex--justify-nowrap label--selectable-left-indent margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
          disabled,
        }}`}
      >
        <label
          className="flex--justify-nowrap flex-grow mw-unset margin-bottom-0"
          htmlFor={warehouse.id}
          data-dropdown-prevent-close
        >
          <span className="flex margin-right-10">
            <span className="margin-right-5">
              <Checkbox
                mode="fancy"
                className="fs-00 v-align-middle"
                id={warehouse.id}
                checked={selected}
                onClick={(value, event) => {
                  if (!disabled) {
                    onSelect(option, event)
                  }
                }}
              />
            </span>
            <span className="label--selectable__text">{option.display}</span>
          </span>
          <span className="wrap--additional-buttons">
            <button
              className="btn btn--link no-underline margin-right-3"
              type="button"
              title="Edit Preset"
              onClick={() => showWarehouseModal({warehouseID: warehouse.id})}
              data-dropdown-prevent-close={false}
            >
              <Icon name="pencil" className="fs-00" />
            </button>
          </span>
        </label>
      </span>
    </li>
  )
}

export default function WarehouseFilter({
  dropdown = 'warehouse_filter_dropdown',
  label = 'Select Warehouse',
  value,
  hasErrors,
  onSelect,
  onChange,
  excludeWarehouseIDs,
  includeSelectAll = false,
}) {
  const isMultiSelect = Array.isArray(value)

  const form = useAutoForm(dropdown, {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    hasChanged: false,
    serverError: null,
  })

  const warehouseOptions = useSelector(warehouseOptionsSelector)
  const warehouseOptionTokens = useSelector(warehouseOptionTokensSelector)

  const onFilter = useCallback(() => {
    simpleFilter({
      formName: dropdown,
      options: warehouseOptions,
      optionTokens: warehouseOptionTokens,
      excludeIDs: excludeWarehouseIDs,
      includeSelectAll,
    })
  }, [warehouseOptions, excludeWarehouseIDs])

  useEffect(onFilter, [warehouseOptions, excludeWarehouseIDs])

  const buttonRef = useRef()

  const localOnChange = useCallback(() => {
    updateForm(dropdown, {hasChanged: true})
  }, [])

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onChange: onFilter,
      onSelect: (...args) => {
        onSelect(...args)

        localOnChange()
      },
    })

  const currentDropdown = useSelector(currentDropdownSelector)

  useEffect(() => {
    if (!currentDropdown && form.hasChanged) {
      updateForm(dropdown, {hasChanged: false})

      if (onChange) {
        onChange()
      }
    }
  }, [currentDropdown])

  return (
    <div
      className={className`wrap-outer--tag-dropdown-with-filter margin-right-20 ${{
        open: currentDropdown === dropdown,
      }}`}
      data-dropdown={dropdown}
    >
      <button
        ref={buttonRef}
        className="btn btn--dropdown btn--filter"
        type="button"
      >
        <strong
          className={className`btn__text ${{'error-message': hasErrors}}`}
        >
          {label}
        </strong>
      </button>
      <FilterDropdown
        dropdown={dropdown}
        form={form}
        onChangeInternal={onChangeInternal}
        onConfirm={onConfirm}
        onUp={onUp}
        onDown={onDown}
        placeholder="Search Warehouses"
        includeSearchInput
        parentRef={buttonRef}
      >
        <>
          {form.results.map((option, index) =>
            option.type === FILTER_SELECT_ALL ? (
              <SelectAllDropdownItem
                key={FILTER_SELECT_ALL}
                option={option}
                selectedList={value}
                isFocused={index === form.selectedIndex}
                onSelect={onSelectInternal}
              />
            ) : isMultiSelect ? (
              <WarehouseCheckboxItem
                key={option.entity.id}
                option={option}
                onSelect={onSelectInternal}
                selected={value.includes(option.entity.id)}
                isFocused={index === form.selectedIndex}
              />
            ) : (
              <WarehouseDropdownItem
                key={option.entity.id}
                option={option}
                onSelect={onSelectInternal}
                selected={value.includes(option.entity.id)}
                isFocused={index === form.selectedIndex}
              />
            ),
          )}
          {form.hasFoundNothing && (
            <li className="list__item--dropdown list__item--dropdown-empty align-center">
              No warehouses match your search.
            </li>
          )}
        </>
      </FilterDropdown>
    </div>
  )
}
